/**
 * Login & Authentication Service
 * Handles all login flows: email/password, Google, SSO (SAML/OIDC)
 */

import { app, auth, db } from './firebase';
import {
    getAuth,
    GoogleAuthProvider,
    OAuthProvider,
    SAMLAuthProvider,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    sendPasswordResetEmail,
    signOut,
    getRedirectResult,
    User,
} from 'firebase/auth';

import {
    setDoc,
    doc,
    updateDoc,
    getDoc,
    getDocs,
    deleteDoc,
    where,
    query,
    collection,
    serverTimestamp,
    increment,
} from 'firebase/firestore';

import { SpotLockUser, isActiveUser, isAdminUser, AuthProvider } from '../sp-types/userType';
import { getAuthConfigByEmail, isAuthMethodAllowed } from '../utils/authConfig';

// ============================================
// SSO Provider Configuration
// ============================================

// Security: This only contains routing info, no secrets (those are in Firebase Console)
export interface SSOProvider {
    id: string;           // Firebase provider ID (e.g., 'oidc.strauss' or 'saml.strauss')
    type: 'oidc' | 'saml'; // Protocol type
    name: string;         // Display name for the button
    orgName: string;      // Organization name for messages
    domains: string[];    // Email domains that use this SSO
    buttonStyle?: {       // Optional custom styling
        bg?: string;
        text?: string;
    };
}

export const SSO_PROVIDERS: SSOProvider[] = [
    // Strauss - Azure AD SAML (Production)
    {
        id: 'saml.strauss-group.com',
        type: 'saml',
        name: 'Strauss SSO',
        orgName: 'Strauss Group',
        domains: ['strauss-group.com'],
        buttonStyle: { bg: '#1a365d', text: 'white' }
    },
    // Moonitor - Microsoft Entra ID SAML (Testing)
    {
        id: 'saml.saml-test',
        type: 'saml',
        name: 'Moonitor SSO',
        orgName: 'Moonitor',
        domains: ['moonitor.cloud'],
        buttonStyle: { bg: '#0078d4', text: 'white' }
    },
];

/**
 * Check if email domain has an SSO provider configured
 */
export function getSSOProviderForEmail(email: string): SSOProvider | null {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return null;
    return SSO_PROVIDERS.find(p => p.domains.includes(domain)) || null;
}

// ============================================
// Login Functions
// ============================================

export interface LoginResult {
    success: boolean;
    uid?: string;
    error?: string;
    errorCode?: string;
    requiresSSO?: boolean;
    ssoProvider?: SSOProvider;
}

/**
 * Sign in with email and password
 */
export async function loginWithEmailPassword(email: string, password: string): Promise<LoginResult> {
    // Check if domain requires SSO
    const ssoProvider = getSSOProviderForEmail(email);
    if (ssoProvider) {
        return {
            success: false,
            error: `Your organization requires ${ssoProvider.name}. Please use the SSO option.`,
            requiresSSO: true,
            ssoProvider,
        };
    }

    console.log('🔐 [LOGIN] Starting email/password login...');
    try {
        const authInstance = getAuth(app);
        const credentials = await signInWithEmailAndPassword(authInstance, email, password);
        console.log('✅ [LOGIN] Email/password authentication successful');
        return { success: true, uid: credentials.user.uid };
    } catch (error: any) {
        console.error('❌ [LOGIN] Email/password login failed:', error.code);
        return { success: false, error: error.message, errorCode: error.code };
    }
}

/**
 * Sign in with Google
 * Returns true if redirect initiated, false if popup completed
 */
export async function loginWithGoogle(): Promise<LoginResult> {
    console.log('🔐 [GOOGLE LOGIN] Starting Google Sign-In...');
    try {
        const provider = new GoogleAuthProvider();
        const authInstance = getAuth(app);
        const isMobile = /Mobi|Android/i.test(navigator.userAgent);

        if (isMobile) {
            signInWithRedirect(authInstance, provider);
            return { success: true }; // Redirect initiated
        } else {
            const result = await signInWithPopup(authInstance, provider);
            console.log('✅ [GOOGLE LOGIN] Google Sign-In successful');
            return { success: true, uid: result.user?.uid };
        }
    } catch (error: any) {
        console.error('❌ [GOOGLE LOGIN] Google Sign-In failed:', error.code);
        return { success: false, error: 'Failed to login with Google', errorCode: error.code };
    }
}

/**
 * Initiate SSO login redirect
 */
export function loginWithSSO(provider: SSOProvider): void {
    console.log('🔐 [SSO] Starting', provider.type.toUpperCase(), 'SSO login redirect for:', provider.name);
    console.log('   Provider ID:', provider.id);
    const ssoProvider = provider.type === 'saml'
        ? new SAMLAuthProvider(provider.id)
        : new OAuthProvider(provider.id);
    signInWithRedirect(auth, ssoProvider);
}

/**
 * Handle redirect result from SSO/Google login
 */
export async function handleRedirectResult(): Promise<{ user: User; authProvider: AuthProvider } | null> {
    try {
        const result = await getRedirectResult(auth);
        console.log('[SSO LOGIN] getRedirectResult result:', result);

        if (result && result.user) {
            console.log('✅ [SSO LOGIN] Redirect authentication successful');
            console.log('👤 [SSO LOGIN] User UID:', result.user.uid);
            console.log('📧 [SSO LOGIN] User email:', result.user.email);

            const providerId = result.providerId || '';
            const authProvider: AuthProvider = providerId.startsWith('saml.') || providerId.startsWith('oidc.') ? 'sso' :
                providerId === 'google.com' ? 'google' : 'email';

            return { user: result.user, authProvider };
        }

        console.log('[SSO LOGIN] No redirect result - user should login explicitly');
        return null;
    } catch (error: any) {
        console.error('❌ [SSO LOGIN] Redirect login failed', error);
        return null;
    }
}

/**
 * Send password reset email
 */
export async function sendResetEmail(email: string): Promise<{ success: boolean; error?: string }> {
    try {
        await sendPasswordResetEmail(auth, email);
        return { success: true };
    } catch (error: any) {
        console.error('Failed to send reset email:', error);
        return { success: false, error: error.message || 'Failed to send reset email.' };
    }
}

// ============================================
// Post-Login Processing
// ============================================

export interface PostLoginResult {
    success: boolean;
    userDocId?: string;
    userData?: SpotLockUser;
    isAdmin?: boolean;
    error?: string;
    errorType?: 'not_found' | 'no_account' | 'inactive' | 'sso_required' | 'method_not_allowed' | 'permission_denied' | 'unknown';
}

/**
 * Process successful authentication - lookup/migrate user doc, validate status
 */
export async function processPostLogin(uid: string, authProvider: AuthProvider = 'email'): Promise<PostLoginResult> {
    console.log('\n🎯 [POST LOGIN] Starting post-login flow...');
    console.log('👤 [POST LOGIN] UID:', uid, 'Provider:', authProvider);

    try {
        let docRef = doc(db, 'users', uid);
        let data = await getDoc(docRef);
        let userDocId = uid;

        // For Google/SSO users, if doc not found by UID, try lookup by email
        if (!data.exists() && (authProvider === 'google' || authProvider === 'sso')) {
            console.log('🔍 [POST LOGIN] Doc not found by UID, trying email lookup...');
            const currentUser = auth.currentUser;
            if (currentUser?.email) {
                // Use lowercase for case-insensitive email matching
                const emailLower = currentUser.email.toLowerCase();
                const q = query(collection(db, 'users'), where('email', '==', emailLower));
                const snapshot = await getDocs(q);
                if (snapshot.docs.length > 0) {
                    const oldDocId = snapshot.docs[0].id;
                    const oldDocData = snapshot.docs[0].data();
                    console.log('✅ [POST LOGIN] Found user by email, oldDocId:', oldDocId);

                    // Migrate: Create new doc with auth UID, copy data, delete old
                    if (oldDocId !== uid) {
                        console.log('🔄 [POST LOGIN] Migrating user doc to auth UID:', uid);
                        const newDocRef = doc(db, 'users', uid);

                        // Create new doc with auth UID
                        await setDoc(newDocRef, {
                            ...oldDocData,
                            id: uid,
                            migratedFrom: oldDocId,
                            migratedAt: serverTimestamp(),
                        });

                        // Delete old doc
                        await deleteDoc(doc(db, 'users', oldDocId));
                        console.log('✅ [POST LOGIN] Migration complete');

                        // Use the new doc
                        docRef = newDocRef;
                        data = await getDoc(newDocRef);
                        userDocId = uid;
                    } else {
                        data = snapshot.docs[0] as any;
                        userDocId = oldDocId;
                        docRef = doc(db, 'users', userDocId);
                    }
                }
            }
        }

        if (!data.exists()) {
            console.error('❌ [POST LOGIN] User document not found');
            await signOut(auth);
            return {
                success: false,
                error: 'Access denied. You need an invitation from an administrator to use this application.',
                errorType: 'not_found',
            };
        }

        const userData = data.data() as SpotLockUser;
        console.log('✅ [POST LOGIN] User:', userData?.email);
        console.log('   Role:', userData?.role, '| Status:', userData?.status);
        console.log('   Legacy - isAdmin:', userData?.isAdmin, '| isApproved:', userData?.isApproved);

        // Check accountId
        if (!userData?.accountId) {
            console.error('❌ [POST LOGIN] No accountId');
            await signOut(auth);
            return {
                success: false,
                error: 'Your account is not associated with an organization. Please contact your administrator.',
                errorType: 'no_account',
            };
        }

        // BACKEND ENFORCEMENT: Check if auth method is allowed by account
        const userEmail = userData?.email || auth.currentUser?.email;
        if (userEmail) {
            const accountConfig = await getAuthConfigByEmail(userEmail);
            console.log('🔒 [POST LOGIN] Account auth config:', accountConfig);

            if (accountConfig.requireSSO && authProvider !== 'sso') {
                console.error('❌ [POST LOGIN] Account requires SSO but user logged in with:', authProvider);
                await signOut(auth);
                return {
                    success: false,
                    error: `Your organization (${accountConfig.accountName || 'Unknown'}) requires SSO login. Please use the SSO option.`,
                    errorType: 'sso_required',
                };
            }

            // Check if the specific auth method is allowed
            if (!isAuthMethodAllowed(accountConfig, authProvider as any)) {
                console.error('❌ [POST LOGIN] Auth method not allowed:', authProvider);
                await signOut(auth);
                return {
                    success: false,
                    error: 'This login method is not allowed for your organization. Please contact your administrator.',
                    errorType: 'method_not_allowed',
                };
            }
        }

        // Check user is active (supports both new status and legacy isApproved)
        if (!isActiveUser(userData)) {
            const status = userData?.status || (userData?.isApproved ? 'active' : 'pending');
            console.warn('⚠️ [POST LOGIN] User not active:', status);
            const messages: Record<string, string> = {
                pending: 'Your account is pending approval. Please contact your administrator.',
                suspended: 'Your account has been suspended. Please contact your administrator.',
                deactivated: 'Your account has been deactivated.',
            };
            await signOut(auth);
            return {
                success: false,
                error: messages[status] || 'Your account is not active. Please contact your administrator.',
                errorType: 'inactive',
            };
        }

        // Update login tracking (fire and forget)
        updateDoc(docRef, {
            lastLoginAt: serverTimestamp(),
            loginCount: increment(1),
            authProvider: authProvider,
            updatedAt: serverTimestamp(),
        }).catch(err => console.warn('Failed to update login tracking:', err));

        console.log('✅ [POST LOGIN] Login complete');
        return {
            success: true,
            userDocId,
            userData,
            isAdmin: isAdminUser(userData),
        };

    } catch (error: any) {
        console.error('❌ [POST LOGIN] Error:', error);
        await signOut(auth);

        // Handle specific errors
        if (error.code === 'permission-denied' || error.message?.includes('insufficient permissions')) {
            console.error('❌ [POST LOGIN] Permission denied - user may not be active');
            return {
                success: false,
                error: 'Access denied. Your account may be pending approval or inactive. Please contact your administrator.',
                errorType: 'permission_denied',
            };
        }

        return {
            success: false,
            error: 'Login failed. Please try again.',
            errorType: 'unknown',
        };
    }
}

/**
 * Full logout - clear session and sign out from Firebase
 */
export async function logout(): Promise<void> {
    sessionStorage.removeItem('uid');
    await signOut(auth);
}
